import { MultiDirectedGraph, DirectedGraph, NotFoundGraphError } from 'graphology';
import { topologicalSort } from 'graphology-dag';
import { allSimplePaths } from 'graphology-simple-path';
import { EdgeEntry } from 'graphology-types';

import { WorkflowNode, ServiceAction, Edge, EdgeType } from '@ply-io/ply-front-shared-utilities';

import { EdgeTypes } from '@/components/FlowTree/flow-tree-service.const';
import AppService from '@/utils/AppService';
import { arrayToObject } from '@/utils/helpers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

type NodeId = string;
type NodeAttributes = { nodeObject: WorkflowNode; action: ServiceAction | null };
type EdgeAttributes = { edgeObject: Edge };
type WorkflowGraph = MultiDirectedGraph<NodeAttributes, EdgeAttributes>;
export interface GraphsObject {
	dag: WorkflowGraph | null;
}
type ActionGetter = (actionId: number) => ServiceAction;
type CreateGraphInput = { nodes: WorkflowNode[]; edges: Edge[]; actionGetter: ActionGetter };

const isLoopStartOrEnd = (node: WorkflowNode) => AppService.isSlugOfLoop(node?.service_slug);
export const getSortedGraph = ({ nodes, edges, actionGetter }: CreateGraphInput) => {
	const nodesMap = arrayToObject(nodes);
	const wg = createWorkflowGraph(nodes, edges, actionGetter);

	const notMulti = new DirectedGraph({ allowSelfLoops: false });

	wg.forEachNode((n, attrs) => notMulti.addNode(n, attrs));

	const graphEdges = wg.edges();

	graphEdges.forEach((edge) => {
		const source = wg.source(edge);
		const target = wg.target(edge);
		const attrs = wg.getEdgeAttributes(edge);
		if (
			attrs.edgeObject.type !== EdgeType.ConsolidatedBy &&
			String(attrs.edgeObject.type) !== EdgeTypes.LoopConnector && // Filter out loop connectors that causes cyclic graph.
			!notMulti.hasEdge(source, target)
		) {
			notMulti.addEdge(source, target);
		}
	});
	const sortedNodesIds = topologicalSort(notMulti);
	const sortedNodes = sortedNodesIds.map((id) => nodesMap[id]);
	return sortedNodes;
};

export const getAllSimplePaths = (wg: WorkflowGraph, firstNodeId: string, lastNodeId: string) => {
	if (!wg.hasNode(firstNodeId) || !wg.hasNode(lastNodeId) || firstNodeId === lastNodeId) {
		return [];
	}
	const paths = allSimplePaths(wg, firstNodeId, lastNodeId);
	return paths.map((path: Array<string>) => path.map((nodeId: string) => wg.getNodeAttributes(nodeId).nodeObject));
};

const getAllSimplePathsIds = (wg: WorkflowGraph, firstNodeId: string, lastNodeId: string) => {
	const allPaths = getAllSimplePaths(wg, firstNodeId, lastNodeId);
	return allPaths.map((path) => path.map(({ id }) => String(id)));
};

const getSimplePath = (wg: WorkflowGraph, firstNodeId: string, lastNodeId: string) => {
	const thePath = getAllSimplePathsIds(wg, firstNodeId, lastNodeId);
	return !thePath || thePath.length === 0 ? [] : thePath[0].map((nodeId: string) => nodeId);
};

const getNodeAction = (nodeId: number, nodesMap: Record<string, WorkflowNode>, actionsMap: Record<string, ServiceAction>) => {
	const actionId = nodesMap[nodeId]?.action;
	if (!actionId) {
		return null;
	}
	return actionsMap[actionId];
};

export function isInBranch(
	wg: WorkflowGraph,
	rootNode: string,
	nodesMap: Record<string, WorkflowNode>,
	node: WorkflowNode,
	actionsMap: Record<string, ServiceAction>
) {
	const isNodeBranchRoot = WorkflowNodeService.isBranchRootNode(node, getNodeAction(node.id, nodesMap, actionsMap));
	const isNodeBranchConsolidate = WorkflowNodeService.isConsolidateDataNodeAndAction(node, getNodeAction(node.id, nodesMap, actionsMap));
	if (isNodeBranchRoot || isNodeBranchConsolidate) {
		return true;
	}
	const pathToNode = getSimplePath(wg, rootNode, String(node.id));
	const branchRootsInPath = pathToNode.filter((nodeId) =>
		WorkflowNodeService.isBranchRootNode(nodesMap[nodeId], getNodeAction(Number(nodeId), nodesMap, actionsMap))
	);
	const branchConsolidationsInPath = pathToNode.filter((nodeId) =>
		WorkflowNodeService.isConsolidateDataNodeAndAction(nodesMap[nodeId], getNodeAction(Number(nodeId), nodesMap, actionsMap))
	);
	return branchRootsInPath.length > branchConsolidationsInPath.length;
}

export function isInLoop(
	wg: WorkflowGraph,
	rootNode: string,
	nodesMap: Record<string, WorkflowNode>,
	node: WorkflowNode,
	actionsMap: Record<string, ServiceAction>
) {
	const pathToNode = getSimplePath(wg, rootNode, String(node.id));
	const loopStartsInPath = pathToNode.filter((nodeId) =>
		WorkflowNodeService.isLoopRoot(nodesMap[nodeId], getNodeAction(Number(nodeId), nodesMap, actionsMap))
	);
	const loopEndsInPath = pathToNode.filter((nodeId) =>
		WorkflowNodeService.isLoopEnd(nodesMap[nodeId], getNodeAction(Number(nodeId), nodesMap, actionsMap))
	);
	return loopStartsInPath.length > loopEndsInPath.length;
}

export const createWorkflowGraph = (nodes: WorkflowNode[], edges: Edge[], actionGetter: ActionGetter): WorkflowGraph => {
	if (!nodes?.length) {
		throw new Error('bad nodes provided');
	}

	if (!edges) {
		throw new Error('bad edges provided');
	}

	if (!actionGetter) {
		throw new Error('missing action getter');
	}

	const g = new MultiDirectedGraph<NodeAttributes, EdgeAttributes>({ allowSelfLoops: false });

	nodes.forEach((node) => {
		let action = null;
		if (node.action) {
			action = actionGetter(node.action);
			if (!action) {
				console.warn(`unable to get action. id: ${node.action}`);
				return null;
			}
		}
		g.addNode(String(node.id), { nodeObject: node, action });
	});

	/**
	 * This doesn't make much sense but it's a workaround for us depending on all the graph creation calls
	 * to succeed. We should remove this `catch` after refactoring the graph reactivity and creation to be more robust,
	 * namely: creating the graph only after we got all effects from the server: nodes AND edges in the same call.
	 */
	edges.forEach((edge) => {
		try {
			g.addEdge(String(edge.source), String(edge.target), { edgeObject: edge });
		} catch (e) {
			if (!(e instanceof NotFoundGraphError)) {
				throw e;
			}
		}
	});
	return g;
};

export function nodesInRoute(graph: GraphsObject, firstNodeId: string, lastNodeId: string) {
	const paths: Array<Array<string>> = graph.dag ? getAllSimplePathsIds(graph.dag, firstNodeId, lastNodeId) || [] : [];
	let result: Array<string> = [];
	if (firstNodeId !== lastNodeId || paths.length == 0) {
		result = [String(firstNodeId)];
	}
	paths.forEach((path) => {
		result = result.concat(path);
	});
	return [...new Set(result.filter((node) => !!node))];
}

export function isValidRouteEnd(graph: GraphsObject, firstNodeId: string, lastNodeId: string, actionsMap: Record<string, ServiceAction>) {
	const nodes = nodesInRoute(graph, firstNodeId, lastNodeId);
	if (!graph.dag) {
		throw new Error('missing workflow graph');
	}
	return nodes.includes(String(lastNodeId)) && inSameHierarchy(graph.dag, nodes, actionsMap);
}

const inSameHierarchy = (wg: WorkflowGraph, nodeIds: Array<string>, actionsMap: Record<string, ServiceAction>) => {
	const nodesMap: Record<string, WorkflowNode> = {};
	nodeIds.forEach((nodeId) => {
		const { nodeObject } = wg.getNodeAttributes(nodeId);
		nodesMap[nodeId] = nodeObject;
	});
	const branchRootsInPath = nodeIds.filter((nodeId) =>
		WorkflowNodeService.isBranchRootNode(nodesMap[nodeId], getNodeAction(Number(nodeId), nodesMap, actionsMap))
	);
	const branchConsolidationsInPath = nodeIds.filter((nodeId) =>
		WorkflowNodeService.isConsolidateDataNodeAndAction(nodesMap[nodeId], getNodeAction(Number(nodeId), nodesMap, actionsMap))
	);
	const loopNodes = nodeIds.filter((nodeId) => isLoopStartOrEnd(nodesMap[nodeId]));
	const inSameLoop = loopNodes.length % 2 == 0; // even number of start and end nodes
	return branchRootsInPath.length === branchConsolidationsInPath.length && inSameLoop;
};

export const destroy = (graphs: GraphsObject) => graphs?.dag?.clear();

const mapEdgeAttribute = (edge: EdgeEntry<NodeAttributes, EdgeAttributes>) => edge.attributes.edgeObject.id;
const isEdgeOfLoopOrBranch = (edge: Edge) => edge.type === EdgeType.ConsolidatedBy || String(edge.type) === EdgeTypes.LoopConnector;

export class EdgeNotFound extends Error {
	constructor(message: string) {
		super(message);
		this.name = this.constructor.name;
	}
}

export function getSingleOutEdge({
	workflowGraph,
	sourceNodeId,
	edgeType,
}: {
	workflowGraph: WorkflowGraph;
	sourceNodeId: NodeId;
	edgeType: string;
}) {
	const edges = Array.from(workflowGraph.outEdgeEntries(sourceNodeId));
	const filteredEdges = edges.filter((edge) => edge.attributes.edgeObject.type === edgeType);

	if (filteredEdges.length === 0) {
		throw new EdgeNotFound(`No edge of type ${edgeType} found`);
	}

	if (filteredEdges.length !== 1) {
		throw new Error(`Not exactly one edge: ${JSON.stringify(filteredEdges)}`);
	}

	return filteredEdges[0];
}

export function getConsolidateId({ workflowGraph, branchRootNodeId }: { workflowGraph: WorkflowGraph; branchRootNodeId: NodeId }) {
	const edge = getSingleOutEdge({ workflowGraph, sourceNodeId: branchRootNodeId, edgeType: EdgeTypes.ConsolidatedBy });
	return edge.target;
}

export function getLoopEndId({ workflowGraph, loopStartNodeId }: { workflowGraph: WorkflowGraph; loopStartNodeId: NodeId }) {
	const edge = getSingleOutEdge({ workflowGraph, sourceNodeId: loopStartNodeId, edgeType: EdgeTypes.LoopConnector });
	return edge.target;
}

/**
 * Iterate nodes in the graph, skipping members of branches and loops.
 */
export function* iterateFlatGenerator(wg: WorkflowGraph, sourceNodeId: NodeId): Generator<string> {
	if (!wg.hasNode(sourceNodeId)) {
		throw new Error(`Node ${sourceNodeId} not found in the graph`);
	}

	yield sourceNodeId;

	const { nodeObject, action } = wg.getNodeAttributes(sourceNodeId);

	let nextId;

	if (WorkflowNodeService.isBranchRootNode(nodeObject, action)) {
		nextId = getConsolidateId({ workflowGraph: wg, branchRootNodeId: sourceNodeId });
	} else if (WorkflowNodeService.isLoopStart(nodeObject.service_slug, action)) {
		nextId = getLoopEndId({ workflowGraph: wg, loopStartNodeId: sourceNodeId });
	} else {
		try {
			const nextEdge = getSingleOutEdge({ workflowGraph: wg, sourceNodeId, edgeType: EdgeTypes.Next });
			nextId = nextEdge.target;
		} catch (e) {
			if (!(e instanceof EdgeNotFound)) {
				throw e;
			}
		}
	}

	if (nextId) {
		yield* iterateFlatGenerator(wg, nextId);
	}
}

export function iterateFlat(workflowGraph: WorkflowGraph, sourceNodeId: NodeId) {
	return Array.from(iterateFlatGenerator(workflowGraph, sourceNodeId));
}

export const getAncestors = (
	wg: WorkflowGraph,
	rootNodeId: string,
	nodeId: string,
	edgesMap: Record<string, Edge>,
	includeRoot: boolean = true
): string[] => {
	if (!wg.hasNode(nodeId)) {
		return [];
	}

	const incomingEdgeIds = [...wg.inboundEdgeEntries(nodeId)].map(mapEdgeAttribute);

	if (incomingEdgeIds.length === 0) {
		return rootNodeId === nodeId && includeRoot ? [nodeId] : [];
	} else if (incomingEdgeIds.length === 1) {
		const edge = edgesMap[incomingEdgeIds[0]];
		const edgeSource = String(edge.source);
		return [nodeId, ...getAncestors(wg, rootNodeId, edgeSource, edgesMap, includeRoot)];
	} else {
		// Multiple incoming edges handling
		const connectorEdgeId = incomingEdgeIds.find((edgeInGraph) => isEdgeOfLoopOrBranch(edgesMap[edgeInGraph]));
		if (connectorEdgeId) {
			const connectorEdge = edgesMap[connectorEdgeId];
			if (connectorEdge) {
				const edgeSource = String(connectorEdge.source);
				return [nodeId, ...getAncestors(wg, rootNodeId, edgeSource, edgesMap, includeRoot)];
			}
		}
		throw new Error(
			'multiple incoming edges handling not implemented for nodes other than branch-consolidate and loop-end maybe one of those'
		);
	}
};

export const getLastReachableNodeInTreeThatIsNotInBranch = (nodes: WorkflowNode[], edges: Edge[]): WorkflowNode => {
	const edgesSources = edges?.map((edge) => edge.source) || [];
	const nodesWithoutOutgoingEdge = nodes.filter((node) => !edgesSources.includes(node.id));
	const sortedNodesWithoutOutgoingEdge = nodesWithoutOutgoingEdge.sort((a, b) => b.id - a.id);
	return sortedNodesWithoutOutgoingEdge[0] || nodes.find((node) => WorkflowNodeService.isNodeTemp(node)) || nodes.at(-1);
};
